import { inject, Injectable } from '@angular/core';
import { type ApolloCache } from '@apollo/client';
import {
  type CreateReminderNotificationInput,
  type CreateReminderNotificationMutationMutation,
  type CreateReminderNotificationMutationMutationVariables,
  type DestroyNotificationInput,
  type DestroyNotificationMutationMutation,
  type DestroyNotificationMutationMutationVariables,
  type MarkAllAsReadMuationMutation,
  type MarkAllAsReadMuationMutationVariables,
  type NotificationItemFragment,
  type NotificationsQueryQuery,
  type NotificationsQueryQueryVariables,
  type UpdateNotificationInput,
  type UpdateNotificationMutation as UpdateNotificationMutationMutation,
  type UpdateNotificationMutationVariables,
} from '@reconcept-platform/shared/types';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { type NotificationSourceEnum } from '../../schema/schema-enums';
import { SharedGraphQlService } from '../../services/shared-graphql.service';
import { type Require } from '../../utils/type-helpers';
import { batchRemoveAllNotificationsSnippet, CreateReminderNotificationMutation } from './notification.mutations';
import {
  DestroyNotificationMutation,
  MarkAllAsReadMutation,
  NotificationsQuery,
  UpdateNotificationMutation,
} from './notifications.queries';

export type NotificationItemWithSubject = Require<NotificationItemFragment, 'subject'>;

@Injectable({
  providedIn: 'root',
})
export class NotificationsApi {
  private graphqlService = inject(SharedGraphQlService);

  watchFreshNotifications(source?: NotificationSourceEnum[]) {
    this.graphqlService.evictFromRootByFieldName('notifications');

    return this.graphqlService
      .watchQuery<NotificationsQueryQuery, NotificationsQueryQueryVariables>({
        query: NotificationsQuery,
        variables: {
          source,
        },
        errorPolicy: 'all', // ignore errors, just show notifications
      })
      .pipe(
        map((result) =>
          (result.data.notifications.nodes ?? []).filter(
            (notification): notification is NotificationItemWithSubject => !!notification.subject,
          ),
        ),
      );
  }

  updateNotification(input: UpdateNotificationInput) {
    return (
      this.graphqlService
        .mutateV2<UpdateNotificationMutationMutation, UpdateNotificationMutationVariables>({
          mutation: UpdateNotificationMutation,
          variables: {
            input,
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateNotification.notification))
    );
  }

  removeNotification(removeNotification: DestroyNotificationInput) {
    return this.graphqlService.mutateV2<
      DestroyNotificationMutationMutation,
      DestroyNotificationMutationMutationVariables
    >({
      mutation: DestroyNotificationMutation,
      variables: {
        input: removeNotification,
      },
      refetchQueries: [
        {
          query: NotificationsQuery,
        },
      ],
      update: (cache) => {
        cache.modify({
          fields: {
            notifications(existingNotificationsRefs, { DELETE }) {
              return DELETE;
            },
          },
        });
      },
    });
  }

  removeAllNotifications(inputData: DestroyNotificationInput[]): Observable<unknown> {
    const mutations: string[] = [];
    const variables: { destroyNotificationInput: { type: string; value: DestroyNotificationInput } }[] = [];
    const updates: ((cache: ApolloCache) => void)[] = [];

    inputData.forEach((destroyNotificationInput: DestroyNotificationInput) => {
      mutations.push(batchRemoveAllNotificationsSnippet);
      variables.push({
        destroyNotificationInput: {
          type: 'DestroyNotificationInput!',
          value: destroyNotificationInput,
        },
      });

      updates.push((cache) => {
        cache.modify({
          fields: {
            notifications(existingNotificationsRefs, { DELETE }) {
              return DELETE;
            },
          },
        });
      });
    });

    return this.graphqlService.mutateBatch(
      {
        mutations,
        variables,
        updates,
      },
      true, // fail gracefully, we let the call site handle the errors
      0,
    );
  }

  markAllAsRead() {
    return this.graphqlService.mutateV2<MarkAllAsReadMuationMutation, MarkAllAsReadMuationMutationVariables>({
      mutation: MarkAllAsReadMutation,
      variables: {
        input: {},
      },
      update: (cache) => {
        cache.modify({
          fields: {
            notifications(existingNotificationsRefs, { INVALIDATE }) {
              return INVALIDATE;
            },
          },
        });
      },
    });
  }

  createReminder(inputData: CreateReminderNotificationInput) {
    return (
      this.graphqlService
        .mutateV2<CreateReminderNotificationMutationMutation, CreateReminderNotificationMutationMutationVariables>({
          mutation: CreateReminderNotificationMutation,
          variables: {
            input: inputData,
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createReminderNotification.notification))
    );
  }
}
